import { FC, useMemo } from 'react';

import { AnimatePresence } from 'framer-motion';
import { Control, DefaultValues, useWatch } from 'react-hook-form';
import { AnyZodObject, z } from 'zod';

import { Checkbox, Input, useForm } from '@ui-furniture/common/form';
import { Row, RowItem, VStack } from '@ui-furniture/elements';
import { useCurrentUser } from '@ui-furniture/hooks/use-current-user';
import { useEmailDeliverabilityDisclaimer } from '@ui-furniture/hooks/use-email-deliverability-disclaimer';
import { useLoadingCallback } from '@ui-furniture/hooks/use-loading-callback';
import { Form } from '@ui-furniture/lib/form';
import { zod } from '@ui-furniture/lib/utils/zod-validation';
import { SoloAnimation } from '@ui-furniture/ui/animation/solo';
import { LoadingButton } from '@ui-furniture/ui/loading/button';

type FormProps = {
  isPhoneOptional?: boolean;
};
const contactDetailsSchema = ({ isPhoneOptional = true }: FormProps) =>
  z.object({
    contactDetails: z.object({
      firstName: z.string().min(2, 'You name must be at least 2 letters'),
      lastName: z.string().min(2, 'You name must be at least 2 letters'),
      email: z
        .string()
        .email({ message: `This doesn't look like a valid email address` }),
      phone: zod.phone({
        isOptional: isPhoneOptional,
      }),
      updateDefaultContactDetails: z.boolean().optional(),
    }),
  });

export type ContactDetails = z.infer<
  ReturnType<typeof contactDetailsSchema>
>['contactDetails'];

export type AdditionalFormProps<TSchema extends AnyZodObject> = {
  formFields: FC<{
    control: Control<TSchema>;
  }>;
  schema: TSchema;
  defaultFields?: DefaultValues<z.infer<TSchema>>;
};

type Props<TSchema extends AnyZodObject> = {
  formProps?: FormProps;
  additionalForm?: AdditionalFormProps<TSchema>;
  handleSubmit: (
    data: z.infer<TSchema> & {
      contactDetails: ContactDetails;
    }
  ) => Promise<void>;
};

function ContactDetailsInternalForm<TSchema extends AnyZodObject>({
  formProps = {
    isPhoneOptional: true,
  },
  additionalForm = {
    formFields: () => null,
    schema: z.object({}),
    defaultFields: {},
  } as any,
  handleSubmit,
}: Props<TSchema>) {
  const { currentUser } = useCurrentUser();

  if (!currentUser) {
    throw new Error('Current user is required');
  }
  const { firstName, lastName, email, phone } = currentUser;

  const schema = useMemo(() => {
    return contactDetailsSchema(formProps).merge(additionalForm.schema);
  }, [additionalForm.schema]);
  const formControls = useForm<
    ReturnType<typeof contactDetailsSchema>,
    undefined
  >(schema as any, {
    defaultValues: {
      contactDetails: {
        firstName,
        lastName,
        email,
        phone,
        updateDefaultContactDetails: true,
      },
      ...(additionalForm.defaultFields ? additionalForm.defaultFields : {}),
    } as any,
  });

  const [isSubmitting, submitQuoteRequest] = useLoadingCallback(
    async (data) => {
      await handleSubmit(data);
    },
    [handleSubmit]
  );

  const dirtyState = formControls.formState.dirtyFields.contactDetails;
  const areContactDetailsDirty =
    dirtyState?.email ||
    dirtyState?.firstName ||
    dirtyState?.lastName ||
    dirtyState?.phone;

  const formEmailValue = useWatch({
    control: formControls.control,
    name: 'contactDetails.email',
  });

  const emailDisclaimer = useEmailDeliverabilityDisclaimer(formEmailValue);

  return (
    <Form
      form={formControls}
      onSubmit={formControls.handleSubmit(submitQuoteRequest as any)}
    >
      <VStack gap='xl'>
        <VStack>
          <Row>
            <RowItem>
              <Input
                name='contactDetails.firstName'
                label='First Name'
                type='firstName'
                autoComplete='given-name'
              />
            </RowItem>
            <RowItem>
              <Input
                name='contactDetails.lastName'
                label='Last Name'
                type='lastName'
                autoComplete='family-name'
              />
            </RowItem>
          </Row>
          <Input
            name='contactDetails.email'
            label='Email'
            type='email'
            autoComplete='email'
          />
          {emailDisclaimer}
          <Input
            name='contactDetails.phone'
            type='phone'
            autoComplete='tel'
            {...(formProps.isPhoneOptional
              ? {
                  label: 'Phone (recommended)',
                  isOptional: true,
                }
              : {
                  label: 'Phone',
                })}
          />
          <AnimatePresence>
            {areContactDetailsDirty ? (
              <SoloAnimation animation='collapse'>
                <Checkbox
                  name='contactDetails.updateDefaultContactDetails'
                  label='Save these details as my default contact details'
                />
              </SoloAnimation>
            ) : null}
          </AnimatePresence>
          {additionalForm.formFields(formControls as any)}
        </VStack>

        <LoadingButton type='submit' isLoading={isSubmitting}>
          Submit
        </LoadingButton>
      </VStack>
    </Form>
  );
}

export function ContactDetailsForm<TSchema extends AnyZodObject>(
  props: Props<TSchema>
) {
  return <ContactDetailsInternalForm {...props} />;
}
